import { stopAudio } from '@/common/utils/audio';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { DEFAULT_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import DefaultConfirmButton_m from '@/game/prefabs/DefaultConfirmButton_m';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';

export interface WalkthroughControllerConfig {
  audioKeys: string[];
  parent: Phaser.GameObjects.Container;
  slides: Phaser.GameObjects.Container[];
  slides_m?: Phaser.GameObjects.Container[];
  continueButton: DefaultButton;
  continueButton_m?: DefaultConfirmButton_m;
  responsiveOverlay: Phaser.GameObjects.Image;
  responsiveOverlay_m?: Phaser.GameObjects.Image;
}

export class WalkthroughController {
  private walkthroughSlideIndex = 0;
  audioKeys: string[] = [];
  currentSound?: Phaser.Sound.BaseSound;
  elements: any;
  onWalkthroughComplete?: () => void;
  hasInitialized = false;

  constructor(private config: WalkthroughControllerConfig) {
    this.audioKeys = config.audioKeys;

    this.setSlidesToIndex(0);

    // Setup interactive overlay interactions.
    [config.responsiveOverlay, config.responsiveOverlay_m].forEach(
      (overlay?: Phaser.GameObjects.Image) => {
        overlay?.setInteractive({
          cursor: `${DEFAULT_CURSOR_PATH}`,
        });
        overlay?.on('pointerdown', () => {
          PhaserAudioManager.playSFX(overlay.scene, 'submit-click');
          this.progressWalkthrough();
        });
      },
    );

    // Setup progress walkthrough buttons.
    [config.continueButton, config.continueButton_m].forEach(
      (continueButton?: DefaultButton | DefaultConfirmButton_m) => {
        continueButton?.controller.setSubmissionCallback(() => {
          this.progressWalkthrough();
        });
      },
    );
  }

  initialize() {
    console.log('init walkthrough');
    if (this.hasInitialized) {
      return;
    }
    this.hasInitialized = true;
    const { config } = this;
    config.parent.setVisible(true);
    PhaserAudioManager.setMusicVolume(
      PhaserAudioManager.DEFAULT_MUSIC_VOLUME / 3,
    );
    this.setSlidesToIndex(0);
    this.currentSound = PhaserAudioManager.playSFX(
      config.parent.scene,
      this.audioKeys[0],
      undefined,
      () => this.showGuidance(true),
      true,
    );
    stopAudio();
    this.showGuidance(false);
    config.parent.scene.scene.bringToTop('AssessmentWalkthroughPanel');
  }

  progressWalkthrough() {
    const { config } = this;
    this.currentSound?.stop();
    this.showGuidance(false);
    this.walkthroughSlideIndex++;
    this.setSlidesToIndex(this.walkthroughSlideIndex);
    if (this.walkthroughSlideIndex >= config.slides.length) {
      config.parent.setVisible(false);
      this.handleMusicVolume();
      this.onWalkthroughComplete?.();
      EventBus.emit(GAME_EVENTS.WALKTHROUGH_COMPLETED);
    }
    if (this.audioKeys[this.walkthroughSlideIndex]) {
      if (this.currentSound)
        PhaserAudioManager.stopSFX(config.parent.scene, this.currentSound.key);

      this.currentSound = PhaserAudioManager.playSFX(
        config.parent.scene,
        this.audioKeys[this.walkthroughSlideIndex],
        undefined,
        () => this.showGuidance(true),
        true,
      );
    }
  }

  reset() {
    console.log('reset 2');
    const { config } = this;
    if (this.hasInitialized) {
      this.currentSound = PhaserAudioManager.playSFX(
        config.parent.scene,
        this.audioKeys[0],
        undefined,
        () => this.showGuidance(true),
        true,
      );
    } else {
      this.initialize();
    }
    config.parent.setVisible(true);
    stopAudio();
    this.walkthroughSlideIndex = 0;
    PhaserAudioManager.setMusicVolume(
      PhaserAudioManager.DEFAULT_MUSIC_VOLUME / 3,
    );
  }

  showGuidance(show: boolean) {
    const { config } = this;
    if ('continueButton' in config) {
      (config.continueButton as DefaultButton).controller.showGuidance(show);
    }
    if ('continueButton_m' in config) {
      (
        config.continueButton_m as DefaultConfirmButton_m
      ).controller.showGuidance(show);
    }
  }

  setSlidesToIndex(index: number) {
    const { config } = this;
    [config.slides, config.slides_m].forEach((slides?) => {
      slides?.forEach((slide, idx) => {
        const container = slide as Phaser.GameObjects.Container;
        container.setVisible(idx === index);
      });
    });
  }

  // This is a hack to deal with duel music vs normal music.
  // Eventually we'll need some audio engineering.
  handleMusicVolume() {
    if (this.config.parent.scene.scene.key === 'Duel') {
      // Duel music slightly louder than normal. Eventually we'll need some audio engineering.
      PhaserAudioManager.setMusicVolume(
        PhaserAudioManager.DEFAULT_MUSIC_VOLUME + 0.2,
      );
    } else {
      PhaserAudioManager.setMusicVolume(
        PhaserAudioManager.DEFAULT_MUSIC_VOLUME,
      );
    }
  }
}
